import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API_BASE = "http://dietdubacherla.in/diet_backend";

const BATCH_OPTIONS = ["2025-27", "2026-28", "2027-29", "2028-30"];

export default function FacultyDashboard() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));

  const [subject, setSubject] = useState("");
  const [uploadType, setUploadType] = useState("material");
  const [quizLink, setQuizLink] = useState("");
  const [quizBatch, setQuizBatch] = useState(BATCH_OPTIONS[0]);
  const [file, setFile] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [timetables, setTimetables] = useState([]);
  const [message, setMessage] = useState("");
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 820);

  const logout = () => {
    localStorage.removeItem("user");
    navigate("/");
  };

  const fetchMaterials = () => {
    axios
      .get(`${API_BASE}/get_materials.php`)
      .then((res) => setMaterials(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.log(err));
  };

  const fetchTimetables = () => {
    axios
      .get(`${API_BASE}/get_timetables.php?audience=faculty`)
      .then((res) => setTimetables(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    axios
      .get(`${API_BASE}/get_notifications.php`)
      .then((res) => setNotifications(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.log(err));

    fetchMaterials();
    fetchTimetables();
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 820);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const uploadedByMe = useMemo(
    () => materials.filter((item) => item.uploaded_by === user?.name).length,
    [materials, user?.name]
  );

  const uploadMaterial = () => {
    setMessage("");

    if (!user) {
      setMessage("User not found. Please login again.");
      return;
    }

    if (!subject.trim()) {
      setMessage("Enter subject before uploading.");
      return;
    }

    if (uploadType === "material" && !file) {
      setMessage("Select a file before uploading material.");
      return;
    }

    if (uploadType === "quiz" && (!quizLink.trim() || !quizBatch.trim())) {
      setMessage("Enter quiz link and select a batch.");
      return;
    }

    const formData = new FormData();
    formData.append("subject", subject.trim());
    formData.append("uploaded_by", user.name);
    formData.append("type", uploadType);
    formData.append("quiz_link", quizLink.trim());
    formData.append("batch", quizBatch.trim());
    if (file) formData.append("file", file);

    axios
      .post(`${API_BASE}/upload_material.php`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => {
        if (res.data.status === "success") {
          setSubject("");
          setUploadType("material");
          setQuizLink("");
          setQuizBatch(BATCH_OPTIONS[0]);
          setFile(null);
          setMessage(uploadType === "quiz" ? "Quiz link uploaded successfully." : "Material uploaded successfully.");
          fetchMaterials();
        } else {
          setMessage(res.data.message || "Unable to upload material.");
        }
      })
      .catch(() => setMessage("Server error while uploading material."));
  };

  return (
    <div style={{ ...styles.page, ...(isMobile ? styles.pageMobile : {}) }}>
      <header style={{ ...styles.hero, ...(isMobile ? styles.heroMobile : {}) }}>
        <div>
          <p style={styles.eyebrow}>Faculty Portal</p>
          <h1 style={styles.title}>Welcome, {user?.name || "Faculty"}</h1>
          <p style={styles.subtitle}>{user?.college_id ? `ID ${user.college_id}` : "DIET faculty dashboard"}</p>
        </div>
        <button onClick={logout} style={styles.primaryButton}>Logout</button>
      </header>

      <section style={styles.metricsGrid}>
        <MetricCard label="Notifications" value={notifications.length} caption="official updates" tone="#991b1b" />
        <MetricCard label="Materials" value={materials.length} caption="total shared files" tone="#1d4ed8" />
        <MetricCard label="My Uploads" value={uploadedByMe} caption="shared by you" tone="#166534" />
        <MetricCard label="Timetables" value={timetables.length} caption="available files" tone="#b45309" />
      </section>

      <main style={{ ...styles.contentGrid, ...(isMobile ? styles.contentGridMobile : {}) }}>
        <section style={styles.leftColumn}>
          <Panel title="Upload Study Material / Quiz">
            <div style={styles.formGrid}>
              <label style={styles.fieldLabel}>
                Type
                <select value={uploadType} onChange={(e) => setUploadType(e.target.value)} style={styles.input}>
                  <option value="material">Material File</option>
                  <option value="quiz">Quiz Link</option>
                </select>
              </label>
              <label style={styles.fieldLabel}>
                Subject
                <input type="text" placeholder="Subject name" value={subject} onChange={(e) => setSubject(e.target.value)} style={styles.input} />
              </label>
              {uploadType === "quiz" && (
                <label style={styles.fieldLabel}>
                  Batch
                  <select value={quizBatch} onChange={(e) => setQuizBatch(e.target.value)} style={styles.input}>
                    {BATCH_OPTIONS.map((batch) => (
                      <option key={batch} value={batch}>{batch}</option>
                    ))}
                  </select>
                </label>
              )}
              {uploadType === "quiz" ? (
              <label style={styles.fieldLabel}>
                Quiz Link
                <input type="text" placeholder="Paste any quiz link" value={quizLink} onChange={(e) => setQuizLink(e.target.value)} style={styles.input} />
              </label>
              ) : (
              <label style={styles.fieldLabel}>
                File
                <input type="file" onChange={(e) => setFile(e.target.files[0])} style={styles.input} />
              </label>
              )}
            </div>
            {message && <p style={styles.notice}>{message}</p>}
            <button onClick={uploadMaterial} style={styles.submitButton}>{uploadType === "quiz" ? "Upload Quiz Link" : "Upload Material"}</button>
          </Panel>

          <Panel title="Study Materials & Quizzes">
            {isMobile ? (
              <div style={styles.mobileMaterialList}>
                {materials.map((item) => (
                  <article key={item.id} style={styles.mobileMaterialCard}>
                    <div style={styles.mobileMaterialHeader}>
                      <div style={styles.mobileMaterialText}>
                        <strong style={styles.mobileMaterialSubject}>{item.subject}</strong>
                        <span style={styles.mobileMaterialMeta}>{item.type === "quiz" ? "Quiz" : "Material"}</span>
                      </div>
                      {item.type === "quiz" ? (
                        <a href={item.quiz_link} target="_blank" rel="noreferrer" style={styles.tableLink}>
                          Open Quiz
                        </a>
                      ) : (
                        <a href={`${API_BASE}/uploads/${item.file_name}`} target="_blank" rel="noreferrer" style={styles.tableLink}>
                          Download
                        </a>
                      )}
                    </div>
                    <div style={styles.mobileMaterialDetails}>
                      <span>By {item.uploaded_by}</span>
                      <span>{item.created_at}</span>
                      <span>Batch: {item.type === "quiz" ? item.batch || "-" : "All"}</span>
                    </div>
                  </article>
                ))}
                {materials.length === 0 && <EmptyText text="No materials uploaded." />}
              </div>
            ) : (
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Subject</th>
                      <th style={styles.th}>Uploaded By</th>
                      <th style={styles.th}>Date</th>
                      <th style={styles.th}>Batch</th>
                      <th style={styles.th}>Type</th>
                      <th style={styles.th}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {materials.map((item) => (
                      <tr key={item.id}>
                        <td style={styles.td}>{item.subject}</td>
                        <td style={styles.td}>{item.uploaded_by}</td>
                        <td style={styles.td}>{item.created_at}</td>
                        <td style={styles.td}>{item.type === "quiz" ? item.batch || "-" : "All"}</td>
                        <td style={styles.td}>{item.type === "quiz" ? "Quiz" : "Material"}</td>
                        <td style={styles.td}>
                          {item.type === "quiz" ? (
                            <a href={item.quiz_link} target="_blank" rel="noreferrer" style={styles.tableLink}>
                              Open Quiz
                            </a>
                          ) : (
                            <a href={`${API_BASE}/uploads/${item.file_name}`} target="_blank" rel="noreferrer" style={styles.tableLink}>
                              Download
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                    {materials.length === 0 && (
                      <tr>
                        <td colSpan="6" style={styles.emptyCell}>
                          No materials uploaded.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </section>

        <section style={styles.rightColumn}>
          <Panel title="Notifications">
            <div style={styles.listStackScrollable}>
              {notifications.map((item) => (
                <div key={item.id} style={styles.listItemPlain}>
                  <strong>{item.title}</strong>
                  <p style={styles.muted}>{item.message}</p>
                  <span style={styles.dateText}>{item.created_at}</span>
                </div>
              ))}
              {notifications.length === 0 && <EmptyText text="No notifications available." />}
            </div>
          </Panel>

          <Panel title="Timetable">
            <div style={styles.resourceGrid}>
              {timetables.map((item) => (
                <article key={item.id} style={styles.resourceCard}>
                  <div>
                    <strong>{item.title}</strong>
                    <p style={styles.muted}>{item.audience}</p>
                  </div>
                  <a href={item.file_url} target="_blank" rel="noreferrer" style={styles.resourceLink}>View</a>
                </article>
              ))}
              {timetables.length === 0 && <EmptyText text="No timetable uploaded." />}
            </div>
          </Panel>
        </section>
      </main>
    </div>
  );
}

function MetricCard({ label, value, caption, tone }) {
  return (
    <div style={styles.metricCard}>
      <span style={{ ...styles.metricBar, background: tone }} />
      <p style={styles.metricLabel}>{label}</p>
      <strong style={styles.metricValue}>{value}</strong>
      <span style={styles.metricCaption}>{caption}</span>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <section style={styles.panel}>
      <div style={styles.panelHeader}>
        <h2 style={styles.panelTitle}>{title}</h2>
      </div>
      {children}
    </section>
  );
}

function EmptyText({ text }) {
  return <p style={styles.emptyText}>{text}</p>;
}

const styles = {
  page: { width: "100%", minHeight: "100vh", overflowX: "hidden", background: "#f4f6f8", color: "#1f2937", padding: "24px", fontFamily: "Inter, Arial, sans-serif", boxSizing: "border-box" },
  pageMobile: { padding: "14px" },
  hero: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "18px", background: "#7f1d1d", color: "#fff", borderRadius: "8px", padding: "24px", marginBottom: "16px" },
  heroMobile: { alignItems: "stretch", flexDirection: "column" },
  eyebrow: { margin: "0 0 6px", color: "#fecaca", fontSize: "12px", fontWeight: 800, textTransform: "uppercase" },
  title: { margin: 0, fontSize: "clamp(22px, 7vw, 28px)", lineHeight: 1.15, overflowWrap: "anywhere" },
  subtitle: { margin: "8px 0 0", color: "#fee2e2" },
  primaryButton: { background: "#fff", color: "#7f1d1d", border: "none", borderRadius: "6px", padding: "10px 14px", fontWeight: 800, cursor: "pointer" },
  metricsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "16px" },
  metricCard: { position: "relative", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "16px", overflow: "hidden" },
  metricBar: { position: "absolute", left: 0, top: 0, bottom: 0, width: "4px" },
  metricLabel: { margin: 0, color: "#6b7280", fontSize: "13px", fontWeight: 700 },
  metricValue: { display: "block", margin: "8px 0 4px", fontSize: "30px", color: "#111827" },
  metricCaption: { color: "#6b7280", fontSize: "12px" },
  contentGrid: { display: "grid", gridTemplateColumns: "minmax(0, 1.15fr) minmax(320px, 0.85fr)", gap: "16px", alignItems: "start" },
  contentGridMobile: { gridTemplateColumns: "1fr", gap: "12px" },
  leftColumn: { display: "grid", gap: "16px", minWidth: 0 },
  rightColumn: { display: "grid", gap: "16px", minWidth: 0 },
  panel: { minWidth: 0, background: "#fff", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "16px", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" },
  panelHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", marginBottom: "12px" },
  panelTitle: { margin: 0, color: "#111827", fontSize: "18px" },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "10px" },
  fieldLabel: { display: "grid", gap: "6px", color: "#374151", fontSize: "13px", fontWeight: 800, marginBottom: "10px" },
  input: { width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: "6px", padding: "10px", fontSize: "14px", background: "#fff" },
  submitButton: { width: "100%", background: "#991b1b", color: "#fff", border: "none", borderRadius: "6px", padding: "11px", fontWeight: 900, cursor: "pointer" },
  notice: { margin: "8px 0", color: "#991b1b", fontWeight: 800 },
  tableWrap: { width: "100%", maxHeight: "360px", overflow: "auto", border: "1px solid #e5e7eb", borderRadius: "8px" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: "620px" },
  th: { background: "#f9fafb", color: "#374151", textAlign: "left", padding: "11px", borderBottom: "1px solid #e5e7eb", fontSize: "12px", textTransform: "uppercase", position: "sticky", top: 0, zIndex: 1 },
  td: { padding: "11px", borderBottom: "1px solid #f1f5f9", color: "#374151", fontSize: "14px" },
  tableLink: { textDecoration: "none", color: "#fff", background: "#7f1d1d", borderRadius: "6px", padding: "7px 9px", fontSize: "13px", fontWeight: 900 },
  emptyCell: { padding: "18px", color: "#6b7280", textAlign: "center", fontWeight: 700 },
  mobileMaterialList: { display: "grid", gap: "10px" },
  mobileMaterialCard: { display: "grid", gap: "10px", padding: "12px", border: "1px solid #e5e7eb", borderRadius: "8px", background: "#f9fafb" },
  mobileMaterialHeader: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "10px" },
  mobileMaterialText: { display: "grid", gap: "4px", minWidth: 0 },
  mobileMaterialSubject: { color: "#111827", overflowWrap: "anywhere" },
  mobileMaterialMeta: { color: "#6b7280", fontSize: "12px", fontWeight: 800, textTransform: "uppercase" },
  mobileMaterialDetails: { display: "grid", gap: "4px", color: "#6b7280", fontSize: "13px", overflowWrap: "anywhere" },
  listStackScrollable: { display: "grid", gap: "10px", maxHeight: "300px", overflowY: "auto" },
  listItemPlain: { padding: "12px", border: "1px solid #e5e7eb", borderRadius: "8px", background: "#f9fafb" },
  muted: { margin: "4px 0 0", color: "#6b7280", fontSize: "13px" },
  dateText: { display: "block", marginTop: "8px", color: "#9ca3af", fontSize: "12px", fontWeight: 700 },
  resourceGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "10px", maxHeight: "300px", overflowY: "auto", paddingRight: "4px" },
  resourceCard: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", minWidth: 0, padding: "12px", border: "1px solid #e5e7eb", borderRadius: "8px", background: "#f9fafb", overflowWrap: "anywhere" },
  resourceLink: { flex: "0 0 auto", textDecoration: "none", color: "#fff", background: "#7f1d1d", borderRadius: "6px", padding: "7px 9px", fontSize: "13px", fontWeight: 900 },
  emptyText: { margin: 0, padding: "14px", color: "#6b7280", border: "1px dashed #d1d5db", borderRadius: "8px", background: "#f9fafb", fontWeight: 700 },
};
